import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import { backgroundColor, toMonthDayYearFormat, roundOneDecimal } from "src/store/common";
import { herbFMColors, hunDeadFuelColors, noColors, oneDeadFuelColors, tenDeadFuelColors, thouDeadFuelColors, woodyFMColors, gsiColors, kbdiColors } from "src/constants";
import { useAddAlert } from "src/store/actions";
import { useStoreContext } from "src/store/context";
import { getNfdrMinMaxInclTime } from "src/apis/getNfdrsCalls";
import moment from "moment";

type NfdrDailyProps = {
    station: any,
    fuelModel: string,
    startDate: any,
    endDate: any,
}


export const NfdrDaily: React.FC<NfdrDailyProps> = ({ station, fuelModel, startDate,endDate}) =>{
  const [state, dispatch] = useStoreContext();
  const [nfdrDailyData, setNfdrDailyData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const addAlert = useAddAlert(dispatch);
  const [ nfdrsDailyFailCount, setNfdrsDailyFailCount ] = useState<number>(0);

  const nfdrDailyCallback = useCallback((counter: number) => {
    (async () => {
      try {
        const request = {
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD'),
          stationIds: station.station_id?.toString(),
          fuelModel: fuelModel,
          sortBy: 'summary_date',
          sortOrder: 'DESC',
          page: 0,
          perPage: 25
        }

        const response: any = await getNfdrMinMaxInclTime(request, addAlert, state?.user);
        if(response.data ) {
          setNfdrDailyData(response.data);
          setNfdrsDailyFailCount(0)
          if((response._metadata.page ===0 && response._metadata.page_count ===1)||(response._metadata.page=== response._metadata.page_count)){
            setHasMore(false)
          }
          else{
            setHasMore(true)
          }
        }
      } catch (error) {
        console.log('error', error)
        setNfdrsDailyFailCount(counter+1)
      }
    })();
  },[startDate, endDate, station, fuelModel, addAlert, state?.user]);

  const fetchMoreData = useCallback(() =>{
    (async () => {
      try {
        const request = {
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD'),
          stationIds: station.station_id.toString(),
          fuelModel: fuelModel,
          sortBy: 'summary_date',
          sortOrder: 'DESC',
          page: page,
          perPage: 25
        }

        const response: any = await getNfdrMinMaxInclTime(request, addAlert, state?.user);
        
        setNfdrDailyData((prevData) => [...prevData, ...response.data]);
        if((response._metadata.page ===0 && response._metadata.page_count ===1)||(response._metadata.page=== response._metadata.page_count)){
          setHasMore(false)
        }
        else{
          setHasMore(true)
        }
        setPage(page + 1);
      } catch (error) {
        setHasMore(false);
        console.log('error', error)
      }
    })();
  },[startDate, endDate, station, fuelModel, page, addAlert, state?.user]);

  /**
   * Initialize the dataset when modifying the parameters
   */
  useEffect(() => {
    if(station){
      setPage(1);
      setHasMore(true);
      nfdrDailyCallback(nfdrsDailyFailCount);
    }
    if(nfdrsDailyFailCount>0 && nfdrsDailyFailCount<4 && station){
      nfdrDailyCallback(nfdrsDailyFailCount);
    }
    
  },[startDate, endDate, station, fuelModel, nfdrDailyCallback, nfdrsDailyFailCount]);
    
  return (
    <Box>
      {/* Table */}
      <Paper sx ={{width: '99%'}}>
        <TableContainer className="table smooth-scrollbar" id="scrollableTable">
          <TableHead className="tableHead"
            style={{
              position: "sticky",
              top: 0,
              display: 'flex',
              zIndex: 110,
              backgroundColor: "#A1A1A1",
            }}
          >
            <TableRow>
              <TableCell style={{ width: "10%" }}>Date(LST)</TableCell>
              <TableCell >Min 1 hr FM/Time</TableCell>
              <TableCell >Min 10 hr FM/Time</TableCell>
              <TableCell >Min 100 hr FM/Time</TableCell>
              <TableCell >Min 1000 hr FM/Time</TableCell>
              <TableCell >KBDI</TableCell>
              <TableCell >Herb FM</TableCell>
              <TableCell >Woody FM</TableCell>
              <TableCell >GSI</TableCell>
              <TableCell >Max IC/Time</TableCell>
              <TableCell >Max SC/Time</TableCell>
              <TableCell >Max ERC/Time</TableCell>
              <TableCell >Max BI/Time</TableCell>
            </TableRow>
          </TableHead>
          {station?.nfdrs_daily_visible === false &&
            <TableBody>
              <div style={{ padding: '15px' }}>
                No <span style={{ fontWeight: '700' }}>Daily</span> Data Available for this <span style={{ fontWeight: '700' }}>Restricted Station</span>
              </div>
            </TableBody>
          }
          {
            !hasMore && nfdrDailyData?.length===0? (
              station?.nfdrs_daily_visible &&
              <TableBody>
                  <div style={{padding: '15px'}}>
                    No Data Available for this Station for the Selected Date Range and Fuel Model
                  </div>
              </TableBody>
            ):(
              station?.nfdrs_daily_visible &&
              <InfiniteScroll
              dataLength={nfdrDailyData?.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={<p>Loading...</p>}
              scrollableTarget="scrollableTable"
            >
              <TableBody>
                  {station?.nfdrs_daily_visible && nfdrDailyData?.map((row, index) => (
                    <TableRow
                      className="row"
                      key={`${row.summary_date} ${index}`}
                      style={{
                        width: '100%',
                        border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                      }}
                    >
                      <TableCell
                        style={{
                          backgroundColor: "rgb(175 158 158 / 87%)",
                          position: "sticky",
                          left: 0,
                          zIndex: 100,
                          width: "10%"
                        }}
                      >
                        {toMonthDayYearFormat(row.summary_date)}
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.one_hr_tl_fuel_moisture_min, oneDeadFuelColors).bg,
                          color: backgroundColor(row.one_hr_tl_fuel_moisture_min, oneDeadFuelColors).text,
                          border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          
                        }}
                      >
                        {roundOneDecimal(row.one_hr_tl_fuel_moisture_min)} / {row.one_hr_tl_fuel_moisture_min_time}:00
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.ten_hr_tl_fuel_moisture_min, tenDeadFuelColors).bg,
                          color: backgroundColor(row.ten_hr_tl_fuel_moisture_min, tenDeadFuelColors).text,
                          border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: 'black',
                          
                        }}
                      >
                        {roundOneDecimal(row.ten_hr_tl_fuel_moisture_min)} / {row.ten_hr_tl_fuel_moisture_min_time}:00
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.hun_hr_tl_fuel_moisture_min, hunDeadFuelColors).bg,
                          color: backgroundColor(row.hun_hr_tl_fuel_moisture_min, hunDeadFuelColors).text,
                          border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
                        }}
                      >
                        {roundOneDecimal(row.hun_hr_tl_fuel_moisture_min)} / {row.hun_hr_tl_fuel_moisture_min_time}:00
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.thou_hr_tl_fuel_moisture_min, thouDeadFuelColors).bg,
                          color: backgroundColor(row.thou_hr_tl_fuel_moisture_min, thouDeadFuelColors).text,
                          border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
  
                        }}
                      >
                        {roundOneDecimal(row.thou_hr_tl_fuel_moisture_min)} / {row.thou_hr_tl_fuel_moisture_min_time}:00
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.kbdi, kbdiColors).bg,
                          color: backgroundColor(row.kbdi, kbdiColors).text,
                          border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
  
                        }}
                      >
                        {roundOneDecimal(row.kbdi)}
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.herbaceous_lfi_fuel_moisture, herbFMColors).bg,
                          color: backgroundColor(row.herbaceous_lfi_fuel_moisture, herbFMColors).text,
                          border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
  
                        }}
                      >
                        {roundOneDecimal(row.herbaceous_lfi_fuel_moisture)}
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.woody_lfi_fuel_moisture, woodyFMColors).bg,
                          color: backgroundColor(row.woody_lfi_fuel_moisture, woodyFMColors).text,
                          border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
    
                      }}
                      >
                        {roundOneDecimal(row.woody_lfi_fuel_moisture)}
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.gsi, gsiColors).bg,
                          color: backgroundColor(row.gsi, gsiColors).text,
                          border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
    
                      }}
                      >
                        {(row.gsi)}
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.ignition_component_max, noColors).bg,
                          color: backgroundColor(row.ignition_component_max, noColors).text,
                          border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
    
                      }}
                      >
                        {roundOneDecimal(row.ignition_component_max)} / {row.ignition_component_max_time}:00
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.spread_component_max, noColors).bg,
                          color: backgroundColor(row.spread_component_max, noColors).text,
                          border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
    
                      }}
                      >
                        {roundOneDecimal(row.spread_component_max)} / {row.spread_component_max_time}:00
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.energy_release_component_max, noColors).bg,
                          color: backgroundColor(row.energy_release_component_max, noColors).text,
                          border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
    
                      }}
                      >
                        {roundOneDecimal(row.energy_release_component_max)} / {row.energy_release_component_max_time}:00
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: backgroundColor(row.burning_index_max, noColors).bg,
                          color: backgroundColor(row.burning_index_max, noColors).text,
                          border: row.nfdr_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                          borderColor: '#222222',
    
                      }}
                      >
                        {roundOneDecimal(row.burning_index_max)} / {row.burning_index_max_time}:00
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </InfiniteScroll>

            )
          }
          </TableContainer>
        </Paper>    
    </Box>
  );
}
